/**
 * Meraki networks store — list of networks the active key can see.
 *
 * Shared by every page that needs a network selector (debranding, onboarding,
 * provisioning, etc). Cached in-memory; the user can force-refresh via the
 * topbar's "Sync now" button (later wired in via this store).
 */
import { create } from "zustand";
import { get as apiGet, ApiError } from "@/api/client";
import { MerakiNetworkListSchema, type MerakiNetwork } from "@/api/schemas/meraki";
import { MOCK_MERAKI_NETWORKS } from "@/api/fixtures/meraki-networks";

interface State {
  data: MerakiNetwork[];
  status: "idle" | "loading" | "ready" | "error";
  error: string | null;
  load: () => Promise<void>;
}

export const useMerakiNetworks = create<State>((set, get) => ({
  data: [],
  status: "idle",
  error: null,

  load: async () => {
    if (get().status === "loading") return;
    set({ status: "loading", error: null });
    try {
      const raw = await apiGet<unknown>("/meraki/networks/");
      const parsed = MerakiNetworkListSchema.parse(raw);
      set({ data: parsed, status: "ready" });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? `API error ${err.status}`
          : err instanceof Error
            ? err.message
            : "unknown error";
      set({
        data: MOCK_MERAKI_NETWORKS,
        status: "error",
        error: `${message} — showing fixture data`,
      });
    }
  },
}));
